/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { SubAgentCall } from '@shared/contexts/ChatContext';
import { Bot, Loader2, Check, X, Clock } from 'lucide-react';
import { cn } from '@shared/lib/utils';

interface SubAgentCallsListProps {
  calls: SubAgentCall[];
  className?: string;
}

export function SubAgentCallsList({ calls, className }: SubAgentCallsListProps) {
  if (!calls || calls.length === 0) {
    return null;
  }

  return (
    <div className={cn("w-full mt-3", className)}>
      <div className="text-xs font-medium text-muted-foreground mb-2">
        Agent Dispatches ({calls.length})
      </div>
      
      <div className="space-y-2">
        {calls.map((call, index) => (
          <div 
            key={`${call.agentName}-${index}`}
            className={cn(
              "flex flex-col gap-2 p-3 border rounded-lg transition-all duration-200",
              call.status === 'started' && "border-blue-200 bg-green-50 animate-pulse",
              call.status === 'completed' && "border-green-200 bg-green-50",
              call.status === 'failed' && "border-red-200 bg-red-50"
            )}
          >
            {/* Header with agent name and status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">
                  {call.agentName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                </span>
              </div>
              
              <div className="flex items-center gap-1">
                {call.status === 'started' && (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin text-blue-600" />
                    <span className="text-xs text-blue-600">Processing...</span>
                  </>
                )}
                {call.status === 'completed' && (
                  <>
                    <Check className="h-3 w-3 text-green-600" />
                    <span className="text-xs text-green-600">Completed</span>
                  </>
                )}
                {call.status === 'failed' && (
                  <>
                    <X className="h-3 w-3 text-red-600" />
                    <span className="text-xs text-red-600">Failed</span>
                  </>
                )}
              </div>
            </div>

            {/* Query being sent to agent */}
            <div className="bg-white/50 rounded p-2 text-xs">
              <div className="text-muted-foreground mb-1">Query:</div>
              <div className="text-sm">
                {call.query.length > 100 
                  ? `${call.query.substring(0, 100)}...` 
                  : call.query
                }
              </div>
            </div>

            {/* Response from agent (if available) */}
            {call.response && (
              <div className="bg-white/50 rounded p-2 text-xs">
                <div className="text-muted-foreground mb-1">Response:</div>
                <div className="text-sm">
                  {call.response.length > 150 
                    ? `${call.response.substring(0, 150)}...` 
                    : call.response
                  }
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>
                {call.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
